import { Box, Button, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditTimePopover } from "../../components/EditTimePopover/EditTimePopover";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { INK } from "../../theme/tokens";
import type { NurseFullRole, SelfScheduleCalendarDaysData } from "../../utils/models";
import { inverseRolesMap } from "../../utils/utilFunctions";

type TimePickerHeaderProps = {
	selectedDate?: DateTime;
	daysData: SelfScheduleCalendarDaysData;
	onTimeChange: (newStartTime: DateTime, newEndTime: DateTime) => void;
};

export const TimePickerHeader: FC<TimePickerHeaderProps> = ({ selectedDate, daysData, onTimeChange }) => {
	const { t } = useTranslation();
	const { chosenDepartment, activeBedsideNurse } = useHospitalData();
	const [anchorEl, setAnchorEl] = useState<HTMLElement>();

	const nurseRole = useMemo<NurseFullRole | undefined>(() => activeBedsideNurse?.role, [activeBedsideNurse]);

	const { displayStartTime, displayEndTime } = useMemo(() => {
		if (selectedDate) {
			const selectedShift = daysData[selectedDate.toFormat("yyyy-MM-dd")];
			if (selectedShift?.startTime && selectedShift?.endTime) {
				return { displayStartTime: selectedShift.startTime, displayEndTime: selectedShift.endTime };
			}
		}

		return { displayStartTime: null, displayEndTime: null };
	}, [selectedDate, daysData]);

	return (
		!chosenDepartment?.selfScheduling?.locked && (
			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mb: 1, p: 0, borderRadius: "8px", width: "fit-content" }}>
				<Typography sx={{ fontWeight: 600, fontSize: "14px", color: INK.SECONDARY }}>{t("shift.times")}</Typography>
				<Button
					variant="outlined"
					onClick={(event) => setAnchorEl(event.currentTarget)}
					disabled={!selectedDate || !displayStartTime || !displayEndTime}
					sx={{ textTransform: "none", minWidth: 200 }}
				>
					{displayStartTime && displayEndTime ? `${displayStartTime.toFormat("h:mm a")} — ${displayEndTime.toFormat("h:mm a")}` : "--"}
				</Button>

				{anchorEl && nurseRole && displayStartTime && displayEndTime && (
					<EditTimePopover
						startTime={displayStartTime}
						endTime={displayEndTime}
						onTimeChange={({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => {
							onTimeChange(newStartTime, newEndTime);
							setAnchorEl(undefined);
						}}
						onClose={() => setAnchorEl(undefined)}
						anchorEl={anchorEl}
						role={inverseRolesMap[nurseRole]}
						hidePatialShiftLengths
					/>
				)}
			</Box>
		)
	);
};
